import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import Card from '@/components/ui/Card/Card';
import styles from './CarbonFootprint.module.scss';
import WorldMap from './WorldMap/WorldMap.tsx';
import Source from './Source/Source.tsx';
import { periodOptions } from '@/constants/climatePro.ts';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import Select from '@/components/ui/Select/Select.tsx';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';
import Co2Period = App.Enums.Co2Period;

const CarbonFootprint: React.FC = () => {
	const { organizationEmissionTarget } = useWhoamiStore();

	const [activeButton, setActiveButton] = useState<'Source' | 'Geography'>('Source');
	const containerRef = useRef<HTMLDivElement>(null);
	const sourceTextRef = useRef<HTMLSpanElement>(null);
	const geographyTextRef = useRef<HTMLSpanElement>(null);
	const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
	const [period, setPeriod] = useState<Co2Period>('last-12-months');

	const updateIndicator = (textRef: HTMLSpanElement | null) => {
		if (!textRef || !containerRef.current) return;
		const containerRect = containerRef.current.getBoundingClientRect();
		const textRect = textRef.getBoundingClientRect();
		setIndicatorStyle({
			width: textRect.width,
			left: textRect.left - containerRect.left,
		});
	};

	useEffect(() => {
		activeButton === 'Source' ? updateIndicator(sourceTextRef.current) : updateIndicator(geographyTextRef.current);
	}, [activeButton]);

	const availablePeriodOptions = useMemo(() => {
		return periodOptions.filter(
			option => !(option.value === 'current-period' && organizationEmissionTarget === null),
		);
	}, [organizationEmissionTarget]);

	return (
		<>
			<Card className={styles.card}>
				<div className={styles.cardContent}>
					<div className={styles.headerWrapper}>
						<FlexBlock justifyContent="space-between">
							<h1>My carbon footprint</h1>
							<div className={styles.periodDropdownWrapper}>
								<Select
									required
									name="period"
									value={period}
									onChange={period => {
										setPeriod(period as Co2Period);
										trackEvent(productAnalyticsEvents.myClimate.carbonFootprint.overview.interact, {
											deduplicationKey: `period.${period}`,
											postHogEventName: 'changed_period_carbon_footprint',
											postHogProperties: {
												period,
												category: 'carbon-footprint',
											},
										});
									}}
									options={availablePeriodOptions}
									placeholder="Choose period"
									className={styles.selectDropdown}
								/>
							</div>
						</FlexBlock>
					</div>

					<div className={styles.subheaderWrapper}>
						<h2>See the emissions from your recent travel</h2>
					</div>

					<div className={styles.tabsWrapper}>
						<div ref={containerRef} className={styles.buttonContainer}>
							<button
								className={classNames(styles.button, {
									[styles.active]: activeButton === 'Source',
								})}
								onClick={() => {
									setActiveButton('Source');
									trackEvent(productAnalyticsEvents.myClimate.carbonFootprint.overview.interact, {
										deduplicationKey: 'source',
										postHogEventName: 'carbon_footprint_source_clicked',
										postHogProperties: { category: 'carbon-footprint' },
									});
								}}
							>
								<span ref={sourceTextRef}>Source</span>
							</button>
							<button
								className={classNames(styles.button, {
									[styles.active]: activeButton === 'Geography',
								})}
								onClick={() => {
									setActiveButton('Geography');
									trackEvent(productAnalyticsEvents.myClimate.carbonFootprint.overview.interact, {
										deduplicationKey: 'geography',
										postHogEventName: 'carbon_footprint_geography_clicked',
										postHogProperties: { category: 'carbon-footprint' },
									});
								}}
							>
								<span ref={geographyTextRef}>Geography</span>
							</button>
							<div
								className={styles.indicator}
								style={{
									width: indicatorStyle.width,
									left: indicatorStyle.left,
								}}
							/>
						</div>
					</div>

					<div className={styles.dividerWrapper}>
						<div className={styles.dividerStatic}></div>
					</div>

					<div className={styles.mobilePeriodDropdown}>
						<Select
							required
							name="period"
							value={period}
							onChange={period => {
								setPeriod(period as Co2Period);
								trackEvent(productAnalyticsEvents.myClimate.carbonFootprint.overview.interact, {
									deduplicationKey: `period.${period}`,
									postHogEventName: 'changed_period_carbon_footprint',
									postHogProperties: { period, category: 'carbon-footprint' },
								});
							}}
							options={availablePeriodOptions}
							placeholder="Choose period"
							className={styles.selectDropdown}
						/>
					</div>

					<div className={styles.contentWrapper}>
						{activeButton === 'Source' && <Source period={period} />}
						{activeButton === 'Geography' && <WorldMap period={period} />}
					</div>
				</div>
			</Card>
		</>
	);
};

export default CarbonFootprint;
